import {palettes} from "data/palettes";

export const RECENT_COLORS_KEY = "neurobaker.recentColors";
export const RECENT_COLORS_LIMIT = 12;

/** Read a typed or pasted color and return lowercase "#rrggbb", or null.
 * Accepts "#abc", "abc", "#aabbcc", "aabbcc", "#aabbccdd" (alpha is dropped),
 * and "rgb(r, g, b)" / "rgba(r, g, b, a)" as copied from Figma or DevTools. */
export function parseColorInput(value: string): string | null {
  const text = value.trim().toLowerCase();

  const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/.exec(text);
  if (hex) {
    const digits = hex[1];
    if (digits.length === 3) {
      return `#${digits
        .split("")
        .map((d) => d + d)
        .join("")}`;
    }
    return `#${digits.slice(0, 6)}`;
  }

  const rgb = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})(?:[\s,/]+[\d.]+%?)?\s*\)$/.exec(
    text,
  );
  if (rgb) {
    const channels = rgb.slice(1, 4).map(Number);
    if (channels.some((c) => c > 255)) return null;
    return `#${channels.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
  }

  return null;
}

/** Put a color at the front of the recent list, without duplicates. */
export function pushRecentColor(
  list: string[],
  hex: string,
  limit = RECENT_COLORS_LIMIT,
): string[] {
  const color = hex.toLowerCase();
  return [color, ...list.filter((c) => c !== color)].slice(0, limit);
}

export function readRecentColors(): string[] {
  const raw = localStorage.getItem(RECENT_COLORS_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((c): c is string => typeof c === "string" && parseColorInput(c) !== null);
  } catch {
    return [];
  }
}

export function writeRecentColors(list: string[]) {
  localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(list));
}

export type ProductPalette = {name: string; colors: string[]};

/** All product palettes, one entry per distinct name, colors as "#rrggbb". */
export const PRODUCT_PALETTES: ProductPalette[] = Object.entries(palettes).map(
  ([name, colors]) => ({
    name,
    colors: (colors as string[]).map((c) => {
      const parsed = parseColorInput(c);
      if (!parsed) throw new Error(`Invalid color "${c}" in palette "${name}"`);
      return parsed;
    }),
  }),
);

export function findProductPalette(label: string | undefined): ProductPalette | null {
  if (!label) return null;
  return PRODUCT_PALETTES.find((p) => p.name === label) ?? null;
}
